import { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
  Clipboard,
  Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'expo-router';
import { supabase } from '@/lib/supabase';
import { isFounderSystemVisible } from '@/lib/founder/isFounderSystemVisible';
import { isFoundingAthlete } from '@/lib/founder/isFoundingAthlete';
import { computeFounderScore } from '@/lib/founder/computeFounderScore';
import { Settings } from 'lucide-react-native';

interface FounderRow {
  id: string;
  handle: string | null;
  display_name: string;
  email: string | null;
  duel_standard_strength_kg: number | null;
  created_at: string;
  total_workouts: number;
  founderScore: number;
  isFounder: boolean;
}

export default function FounderLeaderboard() {
  const { theme } = useTheme();
  const { session } = useAuth();
  const c = theme.colors;
  const [rows, setRows] = useState<FounderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [rpcError, setRpcError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const userEmail = session?.user?.email;

  useEffect(() => {
    if (isFounderSystemVisible({ email: userEmail })) {
      loadLeaderboard();
    }
  }, [userEmail]);

  if (!isFounderSystemVisible({ email: userEmail })) {
    return null;
  }

  const loadLeaderboard = async () => {
    setLoading(true);
    setRpcError(null);
    try {
      const { data, error } = await supabase.rpc('get_founder_leaderboard_with_email');

      if (error) {
        console.error('[FounderLeaderboard] RPC error:', JSON.stringify(error));
        setRpcError(`RPC error: ${error.message ?? JSON.stringify(error)}`);
        return;
      }
      if (!data) {
        console.warn('[FounderLeaderboard] RPC returned null data');
        setRpcError('RPC returned no data');
        return;
      }
      if ((data as any[]).length === 0) {
        console.warn('[FounderLeaderboard] RPC returned empty array — check DB data');
      }

      const scored: FounderRow[] = (data as any[]).map((p) => {
        const { score } = computeFounderScore({
          strengthScore: p.duel_standard_strength_kg ?? 0,
          workoutCount: p.total_workouts ?? 0,
          streakDays: 0,
          referralCount: 0,
        });
        return {
          ...p,
          founderScore: score,
          isFounder: isFoundingAthlete(p.created_at),
        };
      });

      scored.sort((a, b) => b.founderScore - a.founderScore);
      setRows(scored);
    } catch (e: any) {
      console.error('[FounderLeaderboard] Exception:', e);
      setRpcError(`Exception: ${e?.message ?? String(e)}`);
    } finally {
      setLoading(false);
    }
  };

  const copyEmail = (email: string) => {
    Clipboard.setString(email);
    Alert.alert('Copied', email);
  };

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const router = useRouter();

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: c.background }} edges={['top']}>
      <View style={[styles.header, { borderBottomColor: c.border }]}>
        <View style={{ flex: 1 }}>
          <Text style={[styles.title, { color: c.text }]}>FOUNDER LEADERBOARD</Text>
          <Text style={[styles.subtitle, { color: c.textSecondary }]}>
            Internal {'\u00B7'} {rows.length} users
          </Text>
          {rpcError && (
            <Text style={[styles.subtitle, { color: '#ef4444', marginTop: 4 }]}>
              {rpcError}
            </Text>
          )}
        </View>
        <TouchableOpacity
          onPress={() => router.push('/(dev)/admin-dashboard' as any)}
          style={[styles.adminBtn, { backgroundColor: c.surface, borderColor: c.border }]}
          activeOpacity={0.7}
          accessibilityLabel="Admin Dashboard"
        >
          <Settings size={14} color={c.accent} />
          <Text style={[styles.adminBtnText, { color: c.accent }]}>Admin</Text>
        </TouchableOpacity>
      </View>

      {loading ? (
        <ActivityIndicator color={c.accent} style={{ marginTop: 32 }} />
      ) : (
        <ScrollView contentContainerStyle={{ paddingBottom: 32 }}>
          {rows.map((row, i) => {
            const expanded = expandedId === row.id;
            return (
              <TouchableOpacity
                key={row.id}
                onPress={() => toggleExpand(row.id)}
                activeOpacity={0.75}
              >
                <View style={[styles.row, { borderBottomColor: c.border }]}>
                  <Text style={[styles.rank, { color: c.textSecondary }]}>#{i + 1}</Text>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <Text style={[styles.name, { color: c.text }]}>
                        {row.handle ?? row.display_name}
                      </Text>
                      {row.isFounder && (
                        <Text style={{ fontSize: 10, color: c.accent }}>🏛️ FOUNDING</Text>
                      )}
                    </View>
                    <Text style={[styles.meta, { color: c.textSecondary }]}>
                      {row.total_workouts} workouts · strength {Math.round(row.duel_standard_strength_kg ?? 0)}
                    </Text>
                  </View>
                  <Text style={[styles.score, { color: c.text }]}>
                    {Math.round(row.founderScore)}
                  </Text>
                </View>

                {expanded && (
                  <View style={[styles.contactCard, { backgroundColor: c.surface, borderColor: c.border }]}>
                    <Text style={[styles.contactLabel, { color: c.textSecondary }]}>CONTACT</Text>
                    {row.email ? (
                      <TouchableOpacity onPress={() => copyEmail(row.email!)} style={styles.emailRow}>
                        <Text style={[styles.emailText, { color: c.accent }]}>{row.email}</Text>
                        <Text style={[styles.copyHint, { color: c.textSecondary }]}>tap to copy</Text>
                      </TouchableOpacity>
                    ) : (
                      <Text style={[styles.meta, { color: c.textSecondary }]}>No email on file</Text>
                    )}
                    <Text style={[styles.meta, { color: c.textSecondary, marginTop: 4 }]}>
                      Member since {new Date(row.created_at).toLocaleDateString()}
                    </Text>
                    <Text style={[styles.meta, { color: c.textSecondary }]}>
                      ID: {row.id.slice(0, 8)}…
                    </Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  header: {
    padding: 16,
    borderBottomWidth: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  adminBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 4,
    borderWidth: 1,
  },
  adminBtnText: {
    fontSize: 11,
    fontFamily: 'Inter_700Bold',
    letterSpacing: 1,
  },
  title: {
    fontSize: 13,
    fontFamily: 'Inter_700Bold',
    letterSpacing: 2,
  },
  subtitle: {
    fontSize: 11,
    fontFamily: 'Inter_400Regular',
    marginTop: 2,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    gap: 12,
  },
  rank: {
    fontSize: 13,
    fontFamily: 'Inter_700Bold',
    width: 32,
  },
  name: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
  },
  meta: {
    fontSize: 11,
    fontFamily: 'Inter_400Regular',
    marginTop: 2,
  },
  score: {
    fontSize: 16,
    fontFamily: 'Inter_700Bold',
  },
  contactCard: {
    marginHorizontal: 16,
    marginBottom: 8,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  contactLabel: {
    fontSize: 10,
    fontFamily: 'Inter_700Bold',
    letterSpacing: 1.5,
    marginBottom: 6,
  },
  emailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  emailText: {
    fontSize: 13,
    fontFamily: 'Inter_500Medium',
  },
  copyHint: {
    fontSize: 10,
    fontFamily: 'Inter_400Regular',
  },
});
